import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import UserHeader from "@/components/user/UserHeader";
import Footer from "@/components/layout/Footer";
import UserNavMenu from "@/components/user/UserNavMenu";
import { Search, Database, BarChart2, FileCheck, MapPin, Home } from "lucide-react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";

const Traceability: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  
  // Estado para controlar o pop-up de projetos
  const [projetosDialogOpen, setProjetosDialogOpen] = useState(false);
  
  // Novo filtro para checkboxes
  const [showProjects, setShowProjects] = useState(true);
  const [showCreditGenerators, setShowCreditGenerators] = useState(true);
  
  // Nomes dos geradores de crédito para Moeda Social Carbono
  const mscNomes = [
    "Alberto Lopes",
    "Ângela Maria Coutinho Salles Paiva",
    "Antônia Ilma Moura de Oliveira",
    "Beatriz Leite de Menezes",
    "Claudia de Oliveira Carvalho",
    "Cleonice Silva de Paula",
    "Daiene de Carvalho Ferreira",
    "Denise de Almeida Baptista",
    "Eduardo Higino Santos silva",
    "Fernanda Diniz Travasso",
    "Flávia Travassos",
    "Fiorella Ruth Vilcapoma Jorge",
    "Gilliard Quintanilha Tavares",
    "Iago Andrade Monteiro",
    "Ilza da Penha dos Santos Pires",
    "Janelice de Albuquerque Beltrão",
    "Jorge Lucas Guimarães",
    "Joyce Fernanda de Paula",
    "Juliene de Albuquerque Travasso",
    "Kátia de Sousa Campolino",
    "Kelly Cristina Guimaraes Flausino",
    "Lucia da Silva Cunha",
    "Marcos Antônio Campelo da Silva",
    "Miriam da Silva",
    "Pablo da Silva Felipe",
    "Rafaella da Silva Emidio",
    "Raquel dos Santos Pires",
    "Regina Lucia Pires Cavalcante",
    "Rejane Maria Rodrigues Gomes",
    "Rinaldo Antônio Rodrigues da Silva",
    "Rosilene de Lima Barreto",
    "Silvana Simões de Goes",
    "Silvia Gonçalves Sena de Souza",
    "Thaiane Ferraz Campelo",
    "Tania Cristina Medeiros",
    "Thiago Veludo Couto",
    "Valeska Thomaz Vasconcellos",
    "Wilson Guimarães Filho",
    "Nome Extra 1",
    "Nome Extra 2"
  ];
  const today = new Date().toLocaleDateString('pt-BR');
  function mscId(idx) { return `BLC-102${85 + idx}`; }
  function mscKg(idx) { return ((2 + (idx % 9)) * 500).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  function mscStatus(idx) { return idx % 2 === 0 ? 'Disponível' : 'Aposentado'; }
  function mscDate(idx) { return today; }
  const [showMSC, setShowMSC] = useState(false);
  
  // Novo filtro para mscStatus
  const [mscStatusFilter, setMscStatusFilter] = useState('Todos');
  
  // Novo filtro para mostrar o monitoramento de Alberto Lopes
  const [showAlberto, setShowAlberto] = useState(false);
  const albertoMonitoramento = [
    { mes: 'Junho', geradas: '118,28', evitadas: '72,44', tx: '37.98%' },
    { mes: 'Maio', geradas: '115,20', evitadas: '75,52', tx: '39.60%' },
    { mes: 'Abril', geradas: '135,32', evitadas: '55,40', tx: '29.05%' },
    { mes: 'Março', geradas: '162,57', evitadas: '28,15', tx: '14.76%' },
    { mes: 'Fevereiro', geradas: '185,18', evitadas: '5,54', tx: '2.90%' },
    { mes: 'Janeiro', geradas: '190,72', evitadas: 'NC', tx: '0.00%' },
  ];
  
  // Função para verificar se a rota atual corresponde a um determinado padrão
  const isActivePath = (path: string): boolean => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    // Para projetos, verificamos várias rotas possíveis
    if (path === 'projects') {
      return ['/my-projects', '/project/status', '/project/region', '/all-projects'].some(
        p => location.pathname.startsWith(p)
      );
    }
    return location.pathname === path;
  };
  
  // Função para navegar para a rota desejada
  const handleNavigation = (path: string) => {
    navigate(path);
  };

  // Função para lidar com o clique no botão Projetos
  const handleProjetosClick = () => {
    setProjetosDialogOpen(true);
  };

  // Mock data para a tabela de histórico de créditos
  const creditHistory = [
    {
      id: "BLC-10285",
      origin: "Moeda Social Carbono",
      type: "Redução de Emissões",
      region: "Rio de Janeiro, RJ",
      co2Amount: "10.5",
      status: "Validado",
      date: "10/05/2024",
      blockchain: "0x8e3a...9f21",
    },
    {
      id: "BLC-10286",
      origin: "Plante para Neutralizar",
      type: "Remoção de Emissões",
      region: "Rio de Janeiro, RJ",
      co2Amount: "25.3",
      status: "Compensado",
      date: "15/04/2024",
      blockchain: "0x7b2c...3e45",
    },
    {
      id: "BLC-10287",
      origin: "Secretários pelo Clima",
      type: "Redução de Emissões",
      region: "Nacional",
      co2Amount: "15.0",
      status: "Disponível",
      date: "20/03/2024",
      blockchain: "0x6d1a...2f78",
    },
    {
      id: "BLC-10288",
      origin: "Comunidades Empresariais pelo Clima",
      type: "Redução de Emissões",
      region: "Rio de Janeiro, RJ",
      co2Amount: "30.8",
      status: "Validado",
      date: "01/06/2024",
      blockchain: "0x5c9b...1d67",
    },
    {
      id: "BLC-10289",
      origin: "Rede Nacional Fóruns de Juventudes Água e Clima",
      type: "Remoção de Emissões",
      region: "Nacional",
      co2Amount: "12.5",
      status: "Disponível",
      date: "05/02/2024",
      blockchain: "0x4a8d...0e54",
    }
  ];

  // Novo filtro para checkboxes
  const filteredCredits = creditHistory.filter(credit => {
    const isProject = credit.type === 'Redução de Emissões' || credit.type === 'Remoção de Emissões';
    const isCreditGenerator = credit.origin === 'Moeda Social Carbono';
    return (
      (showProjects && isProject) ||
      (showCreditGenerators && isCreditGenerator)
    ) && credit.id.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Agrupar e somar créditos por projeto se apenas projetos estiverem selecionados
  let tableCredits = filteredCredits;
  if (showProjects && !showCreditGenerators) {
    // Agrupar por projeto (origin)
    const projectMap = new Map();
    filteredCredits.forEach(credit => {
      if (!projectMap.has(credit.origin)) {
        projectMap.set(credit.origin, {
          ...credit,
          co2Amount: 0,
        });
      }
      // Somar os créditos
      projectMap.get(credit.origin).co2Amount += parseFloat(credit.co2Amount);
    });
    // Ajustar para mostrar apenas um por projeto
    tableCredits = Array.from(projectMap.values()).map(credit => ({
      ...credit,
      co2Amount: credit.co2Amount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }));
  }

  // Função para determinar a cor do status
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Validado":
        return "bg-blue-100 text-blue-800";
      case "Disponível":
        return "bg-green-100 text-green-800";
      case "Compensado":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <UserHeader />
      
      {/* Menu horizontal superior */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="flex space-x-6 overflow-x-auto py-3">
          <button 
            className={`${isActivePath('/planos') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/planos')}
          >
            Planos
          </button>
          <button 
            className={`${isActivePath('/dashboard') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/dashboard')}
          >
            Dashboard
          </button>
          <button 
            className={`${isActivePath('projects') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={handleProjetosClick}
          >
            Projetos
          </button>
          <button 
            className={`${isActivePath('/emission') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/emission')}
          >
            Emissão
          </button>
          <button 
            className={`${isActivePath('/traceability') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/traceability')}
          >
            Rastreabilidade
          </button>
          <button 
            className={`${isActivePath('/certificates') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/certificates')}
          >
            Certificados
          </button>
          <button
            className={`${isActivePath('/pregao-dashboard') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/pregao-dashboard')}
          >
            Bolsa de Crédito de Carbono Voluntário
          </button>
          <button 
            className={`${isActivePath('/compliance') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/compliance')}
          >
            Conformidade
          </button>
          <button 
            className={`${isActivePath('/retirement') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/retirement')}
          >
            Aposentadoria
          </button>
          <button 
            className={`${isActivePath('/educational') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/educational')}
          >
            Espaço Educativo
          </button>
        </div>
      </div>
      
      {/* Pop-up de Acesso para Projetos */}
      <Dialog open={projetosDialogOpen} onOpenChange={setProjetosDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">Acesso a Projetos</DialogTitle>
            <DialogDescription className="text-center">
              Selecione o tipo de visualização de projetos que deseja acessar.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/my-projects');
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Meus Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Visualize todos os seus projetos cadastrados</p>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/project/status');
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Status dos Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Acompanhe o status de todos os projetos</p>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/project/region');
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Projetos por Região</h3>
                <p className="text-sm text-gray-500 text-center">Explore projetos por localização geográfica</p>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              window.open('https://banclima.com/projetos', '_blank');
              setProjetosDialogOpen(false);
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Portal de Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Acesse o portal público de projetos do Banclima</p>
              </CardContent>
            </Card>
          </div>
          <DialogFooter className="sm:justify-center">
            <Button 
              variant="outline" 
              onClick={() => setProjetosDialogOpen(false)}
            >
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <div className="flex-grow">
        <div className="container mx-auto px-4 py-6">
          {/* <UserNavMenu /> */}
          
          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h1 className="text-2xl font-bold text-[#0e7cb4] mb-4">Rastreabilidade de Créditos de Carbono</h1>
            
            <div className="flex items-center bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
              <Database className="h-8 w-8 text-blue-500 mr-4 flex-shrink-0" />
              <div>
                <h2 className="text-lg font-semibold text-blue-800">Rastreabilidade</h2>
                <p className="text-blue-700 mt-1">
                  Cada token e certificado gerado é registrado em rede pública ou permissionada,
                  garantindo autenticidade e transparência em todas as transações.
                </p>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-md p-4 mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-2">Legitimidade dos créditos de carbono gerados e adquiridos</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div className="flex items-start">
                  <div className="bg-blue-100 p-2 rounded-md mr-3">
                    <Database className="h-5 w-5 text-blue-700" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Origem</p>
                    <p className="text-sm text-gray-600">Projeto e usuário responsável pela geração do token</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-green-100 p-2 rounded-md mr-3">
                    <BarChart2 className="h-5 w-5 text-green-700" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Tipo de projeto</p>
                    <p className="text-sm text-gray-600">Redução ou remoção de emissões de CO₂</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-amber-100 p-2 rounded-md mr-3">
                    <MapPin className="h-5 w-5 text-amber-700" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Região</p>
                    <p className="text-sm text-gray-600">Localização geográfica do projeto</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-cyan-100 p-2 rounded-md mr-3">
                    <BarChart2 className="h-5 w-5 text-cyan-700" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Quantidade de CO₂</p>
                    <p className="text-sm text-gray-600">Volume de CO₂ equivalente reduzido ou removido</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-purple-100 p-2 rounded-md mr-3">
                    <FileCheck className="h-5 w-5 text-purple-700" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Status</p>
                    <p className="text-sm text-gray-600">Validado, disponível ou compensado</p>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-gray-700 mb-4">
              A transparência e confiabilidade oferecidas pelo registro em sistemas de rastreabilidade são essenciais para investidores e empresas
              que buscam garantir a legitimidade dos créditos de carbono adquiridos.
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-xl font-bold text-[#0e7cb4] mb-4">Histórico de Créditos</h2>
            
            <div className="mb-4 flex">
              <div className="relative flex-grow">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  type="text"
                  placeholder="Buscar por ID do Projeto..."
                  className="pl-10 pr-4 py-2 w-full border-gray-300 rounded-md focus:ring-[#0e7cb4] focus:border-[#0e7cb4]"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID do Projeto</TableHead>
                    <TableHead>Projeto de Origem</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Região</TableHead>
                    <TableHead>KgCO2e</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Data</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableCredits.map((credit) => (
                    credit.origin === 'Moeda Social Carbono' ? (
                      <React.Fragment key={credit.id}>
                        <TableRow>
                          <TableCell className="font-medium">BLC-10285</TableCell>
                          <TableCell>
                            <button className="font-bold text-base sm:text-lg text-[#0e7cb4] underline hover:opacity-80" onClick={() => setShowMSC(v => !v)}>
                              Moeda Social Carbono
                            </button>
                          </TableCell>
                          <TableCell>{credit.type}</TableCell>
                          <TableCell>{credit.region}</TableCell>
                          <TableCell>18.144,57</TableCell>
                          <TableCell>
                            <select
                              className="border rounded px-2 py-1 text-xs sm:text-sm"
                              value={mscStatusFilter}
                              onChange={e => setMscStatusFilter(e.target.value)}
                            >
                              <option value="Todos">Todos</option>
                              <option value="Disponível">Disponível</option>
                              <option value="Aposentado">Aposentado</option>
                            </select>
                          </TableCell>
                          <TableCell>{credit.date}</TableCell>
                        </TableRow>
                        {showMSC && (
                          <TableRow>
                            <TableCell colSpan={7} className="p-0 bg-gray-50">
                              <div className="p-2 overflow-x-auto">
                                <table className="min-w-full text-xs sm:text-sm">
                                  <thead>
                                    <tr className="text-left">
                                      <th className="pr-2">Nome</th>
                                      <th className="pr-2">ID</th>
                                      <th className="pr-2">KgCO2e</th>
                                      <th className="pr-2">Status</th>
                                      <th className="pr-2">Dados</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {mscNomes.map((nome, idx) => {
                                      const status = mscStatus(idx);
                                      if (mscStatusFilter !== 'Todos' && status !== mscStatusFilter) return null;
                                      const isAlberto = nome === 'Alberto Lopes';
                                      return (
                                        <React.Fragment key={nome}>
                                          <tr className="border-b last:border-b-0">
                                            <td className="pr-2 font-semibold whitespace-nowrap">
                                              {isAlberto ? (
                                                <button className="text-[#0e7cb4] underline hover:opacity-80" onClick={() => setShowAlberto(v => !v)}>
                                                  Alberto Lopes
                                                </button>
                                              ) : nome}
                                            </td>
                                            <td className="pr-2 whitespace-nowrap">{mscId(idx)}</td>
                                            <td className="pr-2 whitespace-nowrap">
                                              {isAlberto ? (
                                                <span className="font-semibold text-gray-900">237,04</span>
                                              ) : (
                                                <>{mscKg(idx)} <span className="text-xs text-gray-500">kgCO2e</span></>
                                              )}
                                            </td>
                                            <td className="pr-2 whitespace-nowrap">{status}</td>
                                            <td className="pr-2 whitespace-nowrap">{mscDate(idx)}</td>
                                          </tr>
                                          {isAlberto && showAlberto && (
                                            <tr>
                                              <td colSpan={5} className="p-0 bg-gray-100">
                                                <div className="p-3 overflow-x-auto">
                                                  <table className="min-w-full text-xs sm:text-sm">
                                                    <thead>
                                                      <tr className="text-left">
                                                        <th className="pr-2">Mês de Monitoramento</th>
                                                        <th className="pr-2">Geradas</th>
                                                        <th className="pr-2">Emissões Evitadas</th>
                                                        <th className="pr-2">Tx Redução</th>
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {albertoMonitoramento.map((item) => (
                                                        <tr key={item.mes}>
                                                          <td className="pr-2 font-medium whitespace-nowrap">{item.mes}</td>
                                                          <td className="pr-2 whitespace-nowrap">{item.geradas} <span className="text-[10px] align-super">kgCO2e</span></td>
                                                          <td className="pr-2 whitespace-nowrap">{item.evitadas} <span className="text-[10px] align-super">kgCO2e</span></td>
                                                          <td className="pr-2 whitespace-nowrap">{item.tx}</td>
                                                        </tr>
                                                      ))}
                                                    </tbody>
                                                  </table>
                                                </div>
                                              </td>
                                            </tr>
                                          )}
                                        </React.Fragment>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ) : (
                    <TableRow key={credit.id}>
                      <TableCell className="font-medium">{credit.id}</TableCell>
                      <TableCell>{credit.origin}</TableCell>
                      <TableCell>{credit.type}</TableCell>
                      <TableCell>{credit.region}</TableCell>
                        <TableCell>
                          {(() => {
                            // Gera um número aleatório fixo por projeto (origin), múltiplo de 500, entre 500 e 5000
                            function hashString(str) {
                              let hash = 0;
                              for (let i = 0; i < str.length; i++) {
                                hash = ((hash << 5) - hash) + str.charCodeAt(i);
                                hash |= 0;
                              }
                              return Math.abs(hash);
                            }
                            const min = 1, max = 10; // 1*500=500 até 10*500=5000
                            const tokens = min + (hashString(credit.origin) % (max - min + 1));
                            return (tokens * 500).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                          })()}
                        </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(credit.status)}`}>
                            {credit.status === 'Compensado' ? 'Aposentado' : 'Disponível'}
                        </span>
                      </TableCell>
                      <TableCell>{credit.date}</TableCell>
                    </TableRow>
                    )
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="w-full bg-white border-b border-gray-200 p-0">
              <TabsTrigger value="overview" className="flex-1 py-3">Visão Geral</TabsTrigger>
              <TabsTrigger value="implementation" className="flex-1 py-3">Implementação & Registro</TabsTrigger>
              <TabsTrigger value="monitoring" className="flex-1 py-3">Monitoramento</TabsTrigger>
              <TabsTrigger value="verification" className="flex-1 py-3">Verificação & Tecnologia</TabsTrigger>
            </TabsList>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <TabsContent value="overview" className="mt-0">
                <h2 className="text-xl font-semibold mb-3 text-[#0e7cb4]">Rastreabilidade de Créditos de Carbono</h2>
                <p className="text-gray-700 mb-4">
                  A rastreabilidade da geração de créditos de carbono é um aspecto fundamental para garantir a transparência, 
                  a integridade e a confiabilidade no mercado de carbono. Esse processo envolve a documentação, monitoramento e 
                  verificação de todas as etapas desde a implementação de um projeto que visa reduzir ou remover emissões de 
                  gases de efeito estufa até a emissão e a negociação dos créditos gerados.
                </p>
                <div className="flex items-center justify-center my-6">
                  <img 
                    src="/images/traceability-diagram.svg" 
                    alt="Diagrama de Rastreabilidade" 
                    className="max-w-full h-auto" 
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }} 
                  />
                </div>
                <p className="text-gray-700">
                  A rastreabilidade não apenas proporciona confiança no mercado de carbono, mas também é essencial 
                  para manter a integridade ambiental do sistema de créditos, ajudando a combater as mudanças 
                  climáticas de forma eficaz e responsável.
                </p>
              </TabsContent>
              
              <TabsContent value="implementation" className="mt-0">
                <h2 className="text-xl font-semibold mb-3 text-[#0e7cb4]">Implementação e Registro do Projeto</h2>
                <div className="space-y-4">
                  <div className="border-l-4 border-green-500 pl-4">
                    <h3 className="text-lg font-medium text-gray-800">Projeto de Redução ou Remoção de Emissões</h3>
                    <p className="text-gray-700">
                      O primeiro passo é definir claramente o projeto, seus objetivos, escopo e as metodologias 
                      que serão seguidas. O projeto deve ser registrado em uma plataforma ou programa de certificação, 
                      neste caso está sendo hospedado na plataforma BANCLIMA.
                    </p>
                  </div>
                  
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h3 className="text-lg font-medium text-gray-800">Mapeamento de Atividades</h3>
                    <p className="text-gray-700">
                      Toda a atividade que resulta na redução ou remoção de carbono deve ser documentada, 
                      como reflorestamento, captura de metano, uso de energia renovável, etc. Esta 
                      documentação serve como base para a verificação posterior do impacto real do projeto.
                    </p>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="monitoring" className="mt-0">
                <h2 className="text-xl font-semibold mb-3 text-[#0e7cb4]">Monitoramento Contínuo</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-800">Medição e Coleta de Dados</h3>
                    <p className="text-gray-700">
                      As emissões de carbono são monitoradas continuamente para garantir que as reduções ou remoções 
                      sejam reais e mensuráveis. Isso inclui o uso de tecnologias de medição, como sensores e satélites, 
                      além de relatórios regulares.
                    </p>
                    <ul className="list-disc list-inside text-gray-700 ml-4 mt-2">
                      <li>Sensores IoT para monitoramento em tempo real</li>
                      <li>Imagens de satélite para projetos de reflorestamento</li>
                      <li>Relatórios periódicos de atividade e impacto</li>
                    </ul>
                  </div>
                  
                  <div className="mt-4">
                    <h3 className="text-lg font-medium text-gray-800">Controle de Qualidade</h3>
                    <p className="text-gray-700">
                      Um sistema de auditoria ou revisão independente é implementado para garantir que os dados 
                      de monitoramento sejam precisos e válidos. Este controle é essencial para manter a 
                      integridade do processo e garantir que as reduções de emissões alegadas sejam reais.
                    </p>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="verification" className="mt-0">
                <h2 className="text-xl font-semibold mb-3 text-[#0e7cb4]">Verificação, Certificação e Tecnologia</h2>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-800">Benefícios da Rastreabilidade</h3>
                    <p className="text-gray-700 mb-2">
                      A rastreabilidade garante transparência, confiança e segurança em todas as etapas do ciclo de vida dos créditos de carbono.
                    </p>
                    <ul className="list-disc list-inside text-gray-700 ml-2">
                      <li>Transparência total das transações</li>
                      <li>Prevenção de fraudes e dupla contagem</li>
                      <li>Facilidade de auditoria e verificação</li>
                      <li>Confiança para investidores e compradores</li>
                      <li>Integração com mercados globais</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-medium text-gray-800">Tecnologia de Rastreabilidade</h3>
                    <p className="text-gray-700">
                      A plataforma Banclima utiliza tecnologia de rastreabilidade para garantir a transparência 
                      dos créditos de carbono, criando um registro imutável e transparente de cada transação. 
                      Isso ajuda a evitar fraudes e dupla contagem de créditos, garantindo que cada crédito 
                      seja contabilizado apenas uma vez.
                    </p>
                    <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
                      <h4 className="font-medium text-gray-800 mb-2">Benefícios da Rastreabilidade:</h4>
                      <ul className="list-disc list-inside text-gray-700 ml-2">
                        <li>Registro imutável e transparente de todas as transações</li>
                        <li>Prevenção de fraudes e dupla contagem de créditos</li>
                        <li>Auditabilidade completa do histórico de cada token</li>
                        <li>Verificação instantânea da autenticidade dos créditos</li>
                        <li>Integração simplificada com mercados globais de carbono</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-medium text-gray-800">Compliance e Regulação</h3>
                    <p className="text-gray-700">
                      Dependendo do país ou região, os créditos de carbono podem ser sujeitos a regulamentações 
                      específicas, que exigem que as emissões sejam monitoradas, verificadas e reportadas de acordo 
                      com padrões rigorosos estabelecidos pelas autoridades locais ou internacionais.
                    </p>
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Traceability; 